<template>
  <div class="row text-start">
    <div class="product-item card col-md-12">
      <div class="card-body">
        <div class="row">
          <div class="col-md-2 d-none d-md-block">
            <i class="bi bi-image dummy-productimage"></i>
          </div>
          <div class="col-md-10">
            <div class="row mb-3">
              <div :class="discountSum > 0 ? 'col-md-7 ' : 'col-md-9 '">
                <h5 class="bold mb-0">
                  {{
                    productData ? getValue(productData.customerProductName) : ""
                  }}
                </h5>
                <div>
                  {{
                    productData
                      ? getValue(productData.customerProductNumber)
                      : ""
                  }}
                  {{ productData.name }}
                </div>
                <div class="d-flex mt-1">
                  <div class="pe-5 d-inline-block">
                    {{
                      productData
                        ? getValue(productData.productPackagingName)
                        : ""
                    }}
                  </div>
                  <div class="pe-5 d-inline-block">
                    MOQ:
                    {{
                      productData
                        ? formatNumberWithSpaces(getValue(productData.moq))
                        : ""
                    }}
                    boxes
                    <i class="bi bi-info-circle" v-tooltip="moqInfoBox"></i>
                  </div>
                </div>
              </div>
              <div
                v-if="discountSum > 0 && totalDiscount"
                class="col-md-2 d-flex align-items-start"
              >
                <div class="discount-box">Discount: -{{ totalDiscount }}</div>
              </div>
              <div class="text-end col-md-3">
                <h5 class="bold">
                  {{ totalPrice }}
                </h5>
                <div class="d-flex flex-grow-1 justify-content-end">
                  {{
                    currentPrice
                      ? formatCurrency(
                          getValue(currentPrice),
                          getValue(productData.currency)
                        )
                      : currentPriceInfo
                      ? formatCurrency(
                          getValue(currentPriceInfo.price),
                          getValue(productData.currency)
                        )
                      : ""
                  }}
                  / box
                </div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-12">
                {{ productData ? getValue(productData.description) : "" }}
              </div>
            </div>

            <!-- Artwork version -->
            <div
              class="row mb-1"
              v-if="
                productData.artworks && productData.artworks.some((a) => a.id)
              "
            >
              <div class="col-md-2 pt-2">Artwork version</div>
              <div
                class="col-md-2"
                v-for="artWork in productData.artworks"
                :key="artWork.id"
              >
                <VMenu>
                  <div class="d-flex flex-column align-items-start mb-3">
                    <span
                      class="input-group-text"
                      :title="getArtworkConfig(artWork.part)?.title"
                    >
                      <i
                        :class="getArtworkConfig(artWork.part)?.icon"
                        :title="getArtworkConfig(artWork.part)?.title"
                      ></i>
                    </span>
                    <span>{{ artWork.supplierNumber ?? artWork.name }}</span>
                  </div>
                  <template #popper>
                    {{
                      getArtworkConfig(artWork.part)
                        ? getArtworkConfig(artWork.part)?.tooltip
                        : "No tooltip available"
                    }}
                  </template>
                </VMenu>
              </div>
            </div>

            <!-- Comments etc -->
            <div class="row mb-3">
              <div class="col-md-2 pt-2">Comment</div>
              <div class="col-md-10">
                <textarea
                  class="form-control"
                  v-model="artworkComment"
                  rows="1"
                ></textarea>
              </div>
            </div>

            <!-- Quantity and btn-->
            <div class="row">
              <div class="col-md-2 small py-1 calc-date">
                Calc delivery date on confirmed order<br />
                {{ calculatedDeliveryDate }}
              </div>

              <div class="col-md-2 pt-2">Requested Delivery</div>
              <div class="col-md-3">
                <Datepicker
                  v-model="selectedDate"
                  :min-date="minDate"
                  :format="dateFormat"
                  class="datepicker"
                  :auto-apply="true"
                />
                <div class="small red p-2" v-if="selectedDate === null">
                  Date is required
                </div>
              </div>

              <div class="col-md-3">
                <div class="d-flex align-items-center">
                  <i
                    class="bi bi-info-circle fs-4 d-inline-block float-end"
                    v-tooltip="{
                      content: priceInfoBox,
                    }"
                  ></i>
                  &nbsp;
                  <input
                    type="number"
                    class="form-control"
                    id="customQuantity"
                    ref="quantityBox"
                    :min="productData.moq"
                    v-model="customQuantity"
                    placeholder="Enter quantity..."
                    v-on:keyup="customQuantityChange()"
                  />
                  <div class="small red p-2" v-if="showNotInRange">
                    Quantity is not in quantity range
                  </div>
                  <div
                    class="small red p-2"
                    v-if="
                      customQuantity &&
                      productData.moq &&
                      productData.moq - 1 >= customQuantity
                    "
                  >
                    Min. order quantity is {{ productData.moq }}
                  </div>
                </div>
              </div>
              <div class="col-md-2 text-end">
                <div
                  v-tooltip="{
                    content: hasError(productData)
                      ? 'Cannot add to cart: ' + productData.errorMessage
                      : '',
                  }"
                  :class="
                    hasError(productData) ? 'disabled-button-container' : ''
                  "
                >
                  <button
                    type="button"
                    :class="
                      'btn btn-primary w-100 ' +
                      (hasError(productData) ? 'disabled-button' : '')
                    "
                    :disabled="
                      hasError(productData) ||
                      showNotInRange ||
                      (customQuantity &&
                      customQuantity > 0 &&
                      productData.moq &&
                      selectedDate
                        ? productData.moq - 1 >= customQuantity
                        : true)
                    "
                    v-on:click="addToCart()"
                  >
                    Add to order
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
import { useCartStore } from "@/store/cartStore";
import { toast } from "vue3-toastify";
import { PriceInfo, ProductInformation } from "@/data/client";
import Datepicker from "@vuepic/vue-datepicker";
import { formatDate } from "@/helpers/HelperTool";
import moment from "moment";
import { formatCurrency } from "@/helpers/HelperTool";
import { ArtworkTypes, artworkTypeConfig } from "../../interface/ArtWorkTypes";
import { SelectedArtWork } from "@/interface/SelectedArtWork";
import {
  findPriceRange,
  getEffectiveDiscountPercent,
} from "@/helpers/pricingHelpers";

@Component({
  components: { Datepicker },
})
class ProductItem extends Vue {
  @Prop({ default: null })
  productData!: ProductInformation;

  currentPriceInfo!: PriceInfo | null;
  currentPriceRules!: PriceInfo[];
  selectedQuantity!: number | undefined;
  totalPrice!: string;
  totalDiscount!: string;
  discountSum!: number;

  priceInfoBox!: string;
  moqInfoBox!: string;
  customQuantity!: number | undefined;
  currentDiscount!: number | undefined;
  currentPrice!: number | undefined;
  artworkComment = "";

  ArtworkTypes = ArtworkTypes;
  ArtworkTypesconfig = artworkTypeConfig;
  artworkSelections: Record<string, SelectedArtWork> = {};

  showNotInRange = false;

  cartStore = useCartStore();
  cartCount = 0;
  selectedDate = this.getDefaultDeliveryDate();
  minDate = this.getDefaultRequestedDate();
  calculatedDeliveryDate = this.getDefaultDeliveryDate();
  dateFormat = moment()
    .localeData()
    .longDateFormat("L")
    .replace("YYYY", "yyyy")
    .replace("DD", "dd");

  mounted() {
    if (
      this.productData &&
      this.productData.priceRules &&
      this.productData.priceRules?.length > 0
    ) {
      this.currentPriceRules = this.productData.priceRules;

      this.currentPriceInfo = this.productData.priceRules[0];
      this.selectedQuantity = this.productData.moq;

      if (this.currentPriceInfo) {
        // set initial currentDiscount based on effective discount at MOQ
        const effectiveDiscount = getEffectiveDiscountPercent(
          this.productData,
          this.selectedQuantity!,
          this.currentPriceInfo
        );
        this.currentDiscount = effectiveDiscount;
        this.calculatePrices(this.currentPriceInfo);
      }

      this.fillPriceBoxInfo(this.productData);

      this.moqInfoBox = "Minimum order quantity";

      // Auto-populate artworkSelections
      if (this.productData.artworks && this.productData.artworks.length > 0) {
        this.artworkSelections = {};
        this.productData.artworks.forEach((artWork) => {
          if (artWork.id && artWork.part) {
            this.artworkSelections[artWork.part] = {
              id: artWork.id,
              name: artWork.name ?? "",
            };
          }
        });
      }
    }
  }

  hasError(product: ProductInformation): boolean {
    if (!product.errorMessage) return false;
    return product.errorMessage.length > 0;
  }

  getDefaultRequestedDate() {
    const today = new Date();
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + 42);
    return formatDate(futureDate);
  }

  getDefaultDeliveryDate() {
    const today = new Date();
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + 90);
    return formatDate(futureDate);
  }

  fillPriceBoxInfo(productInfo: ProductInformation) {
    let priceTableHtml = "<table class='price-table'>";
    priceTableHtml +=
      "<tr><th align='right'>Order quantity</th><th align='right'>Price/box</th>";

    const hasMergedDiscounts =
      productInfo.priceRules?.some(
        (priceRule) => priceRule.discount != null && priceRule.discount > 0
      ) ?? false;

    if (hasMergedDiscounts) {
      priceTableHtml += "<th align='right'>Discount</th>";
    }
    priceTableHtml += "</tr>";

    if (
      productInfo &&
      productInfo.priceRules &&
      productInfo.priceRules.length > 0
    ) {
      productInfo.priceRules.map((productRule) => {
        const from = this.formatNumberWithSpaces(productRule.fromQuantity);
        const to = productRule.toQuantity
          ? this.formatNumberWithSpaces(productRule.toQuantity)
          : "";
        const currency = this.formatCurrency(
          productRule.price!,
          productInfo.currency
        );

        priceTableHtml +=
          "<tr><td align='right'>" +
          from +
          " - " +
          to +
          "</td><td align='right'>" +
          currency +
          "</td>";

        if (hasMergedDiscounts) {
          if (productRule.discount != null && productRule.discount > 0) {
            priceTableHtml +=
              "<td align='right'>" +
              this.formatNumber(productRule.discount) +
              "%</td>";
          }
        }
        priceTableHtml += "</tr>";
      });
    }
    priceTableHtml += "</table>";

    // SECTION 2: Separate discounts (when discounts[] is non-empty)
    if (productInfo.discounts && productInfo.discounts.length > 0) {
      let discountTableHtml = "<br/><table class='price-table'>";

      discountTableHtml +=
        "<tr><th align='right'>Order quantity</th><th align='right'>Discount</th></tr>";

      productInfo.discounts.map((disc) => {
        const from = this.formatNumberWithSpaces(disc.fromQuantity);
        const to = disc.toQuantity
          ? this.formatNumberWithSpaces(disc.toQuantity)
          : "";

        discountTableHtml +=
          "<tr><td align='right'>" +
          from +
          " - " +
          to +
          "</td><td align='right'>" +
          this.formatNumber(disc.discount!) +
          "%</td></tr>";
      });
      discountTableHtml += "</table>";

      priceTableHtml += discountTableHtml;
    }
    this.priceInfoBox = priceTableHtml;
  }

  addToCart() {
    const qty = this.customQuantity
      ? this.customQuantity
      : this.selectedQuantity!;

    this.cartStore.addToCart(
      this.productData,
      qty,
      this.currentDiscount!,
      this.artworkSelections,
      this.artworkComment,
      moment(this.selectedDate).toDate()
    );

    const productName: string | undefined =
      this.productData.customerProductName ?? this.productData.name;
    toast.success(productName + " is added to cart", {
      position: "top-center",
    });
  }

  getAddToCartBtnMode() {
    let qtyValue =
      this.customQuantity !== undefined &&
      this.customQuantity !== null &&
      this.customQuantity > 0
        ? this.productData.moq! - 1 >= this.customQuantity!
        : true;

    if (this.artworkSelections && qtyValue) {
      return true;
    }

    return false;
  }

  hasArtworks() {
    return this.productData.artworks?.some((a) => a.id);
  }

  getValue(param: any) {
    return param ? param : "";
  }

  formatNumber(num: number) {
    if (num) {
      return Number(num).toFixed(2);
    }
    return "";
  }

  formatNumberWithSpaces(num: number | undefined): string {
    if (num) {
      return new Intl.NumberFormat("en-US", {
        useGrouping: true,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
        .format(num)
        .replace(/,/g, " ");
    } else {
      return "";
    }
  }

  quantityChange() {
    this.currentPrice = undefined;
    this.customQuantity = undefined;

    this.currentPriceInfo = this.currentPriceRules.filter(
      (x) => x.fromQuantity === this.selectedQuantity
    )[0];

    if (this.selectedQuantity) {
      this.currentPriceInfo = findPriceRange(
        this.selectedQuantity,
        this.currentPriceRules
      );

      if (this.currentPriceInfo) {
        this.calculatePrices(this.currentPriceInfo);
      }
    }
  }

  getArtworkConfig(part: string | undefined) {
    return artworkTypeConfig.find((config) => config.type === part);
  }

  customQuantityChange() {
    // TODO : what task is this?
    console.log("discountSum", this.discountSum); // always console.logs 0
    this.currentDiscount = undefined;
    this.showNotInRange = false;

    if (
      this.customQuantity !== undefined &&
      this.customQuantity >= this.productData.moq!
    ) {
      // Find matching discount value
      const matchingQuantityItem = findPriceRange(
        this.customQuantity,
        this.currentPriceRules
      );

      if (matchingQuantityItem) {
        // Determine effective discount (merged or separate) for this quantity
        const effectiveDiscount = getEffectiveDiscountPercent(
          this.productData,
          this.customQuantity,
          matchingQuantityItem
        );

        this.currentDiscount = effectiveDiscount;
        console.log("currentDiscount", this.currentDiscount); // console.logs the discount matching the quantity I have inputed
        this.currentPrice = matchingQuantityItem.price;

        this.calculatePrices(matchingQuantityItem);
      } else {
        // Quantity is not within any range in currentPriceRules
        if (this.customQuantity && this.customQuantity > 0) {
          this.showNotInRange = true;
        }
      }
    }
  }

  calculatePrices(priceItem: PriceInfo) {
    if (!priceItem) return;

    const qty = this.customQuantity
      ? this.customQuantity
      : priceItem.fromQuantity;

    const discountPercent =
      this.currentDiscount != null
        ? this.currentDiscount
        : priceItem.discount ?? 0;

    this.currentDiscount = discountPercent;

    const totalSum = priceItem!.price! * qty!;

    if (discountPercent > 0) {
      this.discountSum = totalSum * (discountPercent / 100);
      this.totalDiscount = this.formatCurrency(
        this.discountSum,
        this.productData.currency
      );
    } else {
      this.discountSum = 0;
      this.totalDiscount = "";
    }

    this.totalPrice = this.formatCurrency(
      totalSum - this.discountSum,
      this.productData.currency
    );
  }

  formatCurrency(value: number, currency: string | undefined = "USD"): string {
    return formatCurrency(value, currency);
  }
}

export default ProductItem;
</script>
